/**
 * `files` — what a stored file looks like on the wire, and what a client says when
 * they answer a review.
 *
 * Session 06 built the table and the two read tools without a schema file here,
 * because nothing outside the API needed the shape. Session 16 adds uploads, the
 * gating rule, the review path and the ZIP bundle, and every one of those is a body
 * the SPA will send or a document it will render — so the shapes move here, where
 * `.docs/00-index.md` §5 puts them: "Zod schemas for every entity and every API
 * body — API validates and SPA forms resolve with the same object."
 *
 * Two things in here are the session in miniature.
 *
 *   **`released` is a boolean on the wire and two columns in the database.** A
 *   client is told whether they can download something, not when we decided; the
 *   instant is on the row for the studio and in the audit log for everybody else.
 *
 *   **A review has three states and only two of them are answers.** `null` is "we
 *   never asked", `requested` is "waiting on you" — the "1 awaiting your review" the
 *   files page prints — and `approved` / `changes_requested` are what the client
 *   said. A boolean would have collapsed the first two, and the first two are the
 *   difference between a chase job that fires and one that does not.
 */
import { z } from 'zod';
import { DateOnlySchema, MoneySchema, PeriodSchema, Rfc3339Schema } from './primitives.js';
import { ReportStatusSchema } from './reports.js';
/* ------------------------------------------------------------------- the review */
/** What a client may answer with. `mockups/client/files.html` gives two buttons. */
export const FILE_REVIEW_OUTCOMES = ['approved', 'changes_requested'];
export const FileReviewOutcomeSchema = z
    .enum(FILE_REVIEW_OUTCOMES)
    .describe('What the client said: approved, or changes_requested.');
/**
 * Where a file stands in the review cycle, as one word.
 *
 * Derived from the row's three stamps rather than stored: `none` when nobody asked,
 * `requested` while it is waiting, and the client's own answer once they give one.
 * `fileReviewStateOf` below is the only derivation, so the API, the read models and
 * the SPA all read the same word off the same three columns.
 */
export const FILE_REVIEW_STATES = ['none', 'requested', ...FILE_REVIEW_OUTCOMES];
export const FileReviewStateSchema = z
    .enum(FILE_REVIEW_STATES)
    .describe('Where the file is in its review: none (nobody asked), requested (waiting on the ' +
    'client), approved, or changes_requested.');
/**
 * The one derivation of a review state.
 *
 * An answer wins over a request, because staff re-asking is what reopens a review
 * (`files_request_review` clears the answer) and a row that carried both would mean
 * the reopening did not happen.
 */
export function fileReviewStateOf(stamps) {
    if (stamps.answeredAt !== null && stamps.outcome !== null)
        return stamps.outcome;
    return stamps.requestedAt === null ? 'none' : 'requested';
}
/** The body of `POST /api/clients/me/files/:id/review`. */
export const FileReviewSchema = z
    .strictObject({
    decision: z
        .enum(['approve', 'changes'])
        .describe('approve when it is right, changes when something has to be redone.'),
    note: z
        .string()
        .max(2_000)
        .default('')
        .describe('What to change, or anything worth saying. It goes to the studio and stays.'),
})
    .describe('A client’s answer on a deliverable that was sent for review. Approving one is not a ' +
    'money decision and any member may do it.');
/* -------------------------------------------------------------------- the file */
/**
 * One stored file, as the portal and the tools publish it.
 *
 * `path` is deliberately absent, for the reason `modules/files/service.ts` gives:
 * it is what an `X-Accel-Redirect` is built from, and a caller who knew it would
 * have a URL that skips the authorisation the download route exists to do.
 */
export const FileSchema = z
    .object({
    id: z.string().describe('The file’s id. Quote it to a person, or pass it to files_get.'),
    kind: z
        .string()
        .describe('deliverable, invoice_pdf, report_pdf, brand, handover, upload, other.'),
    name: z.string().describe('The original filename, as it was uploaded.'),
    title: z
        .string()
        .describe('The display name the files page prints in bold — "Reels — 14 edits, 9:16". Falls ' +
        'back to the filename when nobody typed one.'),
    mime: z.string().describe('The content type it was stored with.'),
    size: z.int().describe('Bytes.'),
    sha256: z.string().describe('The digest of the bytes, lowercase hex.'),
    period: PeriodSchema.nullable().describe('The month a deliverable belongs to, YYYY-MM. Null for anything with no month.'),
    work_order_id: z.string().nullable().describe('The project it belongs to, or null.'),
    milestone_id: z.string().nullable().describe('The milestone it belongs to, or null.'),
    released: z
        .boolean()
        .describe('Whether the client can download it. False for a handover we are holding.'),
    released_at: Rfc3339Schema.nullable().describe('When it became downloadable. Null means we are holding it.'),
    archived_at: Rfc3339Schema.nullable().describe('When it was hidden from the client, or null.'),
    review: FileReviewStateSchema,
    review_requested_at: Rfc3339Schema.nullable().describe('When we asked for a review, or null.'),
    review_request_note: z
        .string()
        .describe('What the studio asked them to look at, in the studio’s own words. Empty when we ' +
        'asked without saying anything, and rewritten by every fresh request.'),
    reviewed_at: Rfc3339Schema.nullable().describe('When the client answered, or null.'),
    review_note: z.string().describe('What the client said when they answered. Empty when none.'),
    created_at: Rfc3339Schema.describe('When it was registered.'),
})
    .describe('One stored file. The path on disk is deliberately not published.');
/* ---------------------------------------------------------- client read models */
export const FileEntrySchema = FileSchema.extend({
    services: z.array(z.string()).describe('Services whose deliverables name this file.'),
    delivered_on: z.array(DateOnlySchema).describe('Days those deliverables were delivered.'),
    milestone_seq: z.int().nullable().describe('Resolved milestone number, or null.'),
    milestone_title: z.string().nullable().describe('Resolved milestone title, or null.'),
    milestone_delivered_at: Rfc3339Schema.nullable().describe('When that milestone was delivered, or null.'),
}).describe('One file entry with the cross-module context the files page needs.');
const DeliverableCountsSchema = z
    .object({
    service: z.string().describe('Rate-card service code.'),
    content_type: z.string().nullable().describe('Content type, or null for a non-content unit.'),
    qty: z.number().positive().describe('Quantity delivered.'),
})
    .describe('A delivered quantity represented by files in a month.');
export const FilesByMonthSchema = z
    .object({
    month: z.object({
        period: PeriodSchema,
        files: z.int().nonnegative().describe('Stored file rows in the month.'),
        grouped_files: z
            .number()
            .nonnegative()
            .describe('Deliverable quantities behind the displayed file groups, counted once.'),
        bytes: z.int().nonnegative(),
        all_released: z.boolean(),
        awaiting_review: z.int().nonnegative(),
        entries: z.array(FileEntrySchema),
    }),
    earlier: z.array(z.object({
        period: PeriodSchema,
        files: z.int().nonnegative(),
        bytes: z.int().nonnegative(),
        items: z.array(DeliverableCountsSchema),
    })),
    total_files: z.int().nonnegative(),
    total_bytes: z.int().nonnegative(),
    latest_period: PeriodSchema.nullable().describe('The newest month this client has files in that has already begun, or null when they ' +
        'have none. What the page opens on with no `?period=`, and what the period control ' +
        'counts back from — session 41 (B1): the browser’s current month is not a fact about ' +
        'the client’s deliveries. Never a future month, because a file may be filed under one.'),
    awaiting_review_any_month: z
        .int()
        .nonnegative()
        .describe('Files awaiting this client’s review in **any** month. The one source for that count: ' +
        'the nav badge, the dashboard line and this page all read it, so the three cannot ' +
        'disagree. `month.awaiting_review` beside it still means the month on screen.'),
})
    .describe('The month tab, strictly earlier months, and client disk totals.');
export const ProjectFilesSchema = z
    .object({
    work_order_ref: z.string(),
    title: z.string(),
    kind: z.string(),
    entries: z.array(FileEntrySchema),
    locked: z
        .object({
        count: z.int().positive(),
        items: z.array(z.object({ title: z.string(), kind: z.string() })),
        milestone: z
            .object({
            seq: z.int().positive(),
            title: z.string(),
            amount: MoneySchema,
            due_on: DateOnlySchema.nullable(),
            delivered: z.boolean(),
        })
            .nullable(),
    })
        .nullable(),
})
    .describe('One project card, including its payment-gated handover block.');
export const FilesByProjectSchema = z.array(ProjectFilesSchema);
export const ReportLibraryEntrySchema = z
    .object({
    period: PeriodSchema,
    status: ReportStatusSchema,
    headline: z.string(),
    issued_at: Rfc3339Schema.nullable(),
    pdf: FileEntrySchema.nullable(),
})
    .describe('A draft or issued monthly report and its PDF when rendering has completed.');
export const FilesLibrarySchema = z
    .object({
    reports: z.array(ReportLibraryEntrySchema).describe('Reports newest first, drafts included.'),
    report_pdf_count: z.int().nonnegative().describe('Released report PDFs currently available.'),
    brand: z.array(FileEntrySchema),
})
    .describe('The reports and brand-assets column of the files page.');
export const ClientFilesPageSchema = FilesByMonthSchema.extend({
    projects: FilesByProjectSchema,
    library: FilesLibrarySchema,
}).describe('The complete client files-page REST response.');
const FilesContentsLineSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('month').describe('One calendar month of deliverables.'),
        period: PeriodSchema,
        files: z.int().nonnegative().describe('How many files that month has.'),
        bytes: z.int().nonnegative().describe('Their total size, in bytes.'),
        all_released: z.boolean().describe('True when every file in it has been released.'),
        held: z.int().nonnegative().describe('How many of them are still held, none or all.'),
    }),
    z.object({
        kind: z.literal('project').describe('One work order’s own file group.'),
        work_order_ref: z.string().describe('The project or extra it belongs to.'),
        title: z.string().describe('The work order’s own title.'),
        files: z.int().nonnegative().describe('How many files it has.'),
        bytes: z.int().nonnegative().describe('Their total size, in bytes.'),
        all_released: z.boolean().describe('True when every file in it has been released.'),
        held: z.int().nonnegative().describe('How many of them are still held, none or all.'),
    }),
]);
/**
 * Why the held files are held — session 24 (R3-17).
 *
 * The studio's Files panel says "Only a project handover is gated on its final
 * invoice — 4 items are still locked", and the claim and the count came from
 * different things: the claim is about the payment gate, and `held` is every file
 * with no `released_at`. The moment the studio held a *deliverable* the count went to
 * five and the sentence stayed, which made it false — `CLAUDE.md` §Files: "**Held is
 * not gated** — a client's own brief is held for ever and is behind no payment."
 *
 * One object rather than two numbers, because the two only mean anything together: a
 * page holding one of them and not the other could say something untrue, and this way
 * the null is a single null.
 */
export const HeldReasonsSchema = z
    .object({
    gated: z
        .int()
        .nonnegative()
        .describe('Held because a project handover is waiting on its final invoice — files.gatedHandovers.'),
    ungated: z
        .int()
        .nonnegative()
        .describe('Held for some other reason, and behind no payment at all.'),
})
    .describe('The held files, split by why they are held. The two sum to `held`.');
export const ClientFilesSummarySchema = z
    .object({
    files: z.int().nonnegative().describe('Every file this client has, released or held.'),
    bytes: z.int().nonnegative().describe('Their total size, in bytes.'),
    held: z.int().nonnegative().describe('How many are held — not yet released.'),
    /**
     * `.default(null)` because the split is newer than the recorded world
     * (`apps/web/src/test/world/studio.json`): a payload taken before it means "we
     * cannot say why", and the page falls back to the claim it can still make.
     */
    held_reasons: HeldReasonsSchema.nullable()
        .default(null)
        .describe('Why the held files are held, or null in a payload recorded before the split. ' +
        '`held` is their sum either way.'),
    contents: z
        .array(FilesContentsLineSchema)
        .describe('The contents fold: one line per month and per project.'),
})
    .describe('The studio client-detail file totals and contents fold.');
/* ------------------------------------------------------------------ the upload */
/**
 * What `files_create_upload` reserves, before any bytes exist.
 *
 * `ARCHITECTURE.md` §Catalogue: "`files_create_upload` returns a one-time `PUT` URL
 * and the future file ref; the bytes go over HTTP, not through MCP, and the row is
 * finalised when the upload completes." All three of those are here: the id the row
 * will have, the URL the bytes go to, and the moment the URL stops working.
 */
export const FileUploadTicketSchema = z
    .object({
    file_id: z
        .string()
        .describe('The id the file will have once the bytes land. Reserved, not yet a file.'),
    upload_url: z
        .string()
        .describe('PUT the bytes here, with the file’s own media type as Content-Type. One use, then ' +
        'it is spent.'),
    expires_at: Rfc3339Schema.describe('After this the URL is refused and nothing was stored.'),
    max_bytes: z.int().describe('The largest body the upload route will accept.'),
})
    .describe('A reserved file row and the one-time URL its bytes go to.');
/**
 * What comes back when the bytes land.
 *
 * The whole file, plus the flag that answers the one question an uploader has that
 * a second `files_get` would otherwise be needed for: was this the first time these
 * bytes arrived, or is it a duplicate of something already on the disk?
 * `.docs/16-files-calls-reports.md` §Edge cases A: "duplicate upload (same sha256)
 * → new row (different `file_id`), not an error" — so it is a note, never a refusal.
 */
export const FileUploadResultSchema = z
    .object({
    file: FileSchema,
    duplicate_of: z
        .array(z.string())
        .describe('Ids of files this client already has with the same digest. A new row is written ' +
        'either way; this is a note, not a refusal.'),
})
    .describe('The registered file, and whatever else on this account has the same bytes.');
//# sourceMappingURL=files.js.map